import React, { useEffect, useRef } from 'react';
import { useNavigate, NavLink, } from 'react-router-dom';
import { getCountryName, getCountryFlag } from './countryCodeMapping';

import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Format seconds → "2m 06s" / "43s" / "0s" for the Visit Duration column.
const formatDurationShort = (seconds) => {
        const s = Number(seconds);
        if (!s || s <= 0 || Number.isNaN(s)) return '0s';
        const m = Math.floor(s / 60);
        const rem = Math.floor(s % 60);
        if (m === 0) return `${rem}s`;
        return `${m}m ${String(rem).padStart(2, '0')}s`;
};

// Browser / OS icons for the `devices` type rows. Kept in sync with the
// inline icon maps in DevicesInsights.jsx — if you add a browser there, mirror
// it here so the modal and inline list render the same glyph.
const BROWSER_ICONS = {
        'Chrome': '🌐',
        'Chromium': '🌐',
        'Firefox': '🦊',
        'Safari': '🧭',
        'Microsoft Edge': '🌐',
        'Edge': '🌐',
        'Opera': '🔴',
        'Opera Touch': '🔴',
        'Samsung Browser': '🌐',
        'DuckDuckGo Privacy Browser': '🦆',
        'Ecosia': '🌳',
        'Brave': '🦁',
        'Yandex Browser': '🌐',
        'Vivaldi': '🌐',
        'UC Browser': '🌐',
        'QQ Browser': '🐧',
        'Sogou Explorer': '🌐',
        'Whale': '🐳',
        'Silk': '🌐',
        'Maxthon': '🌐',
        'Mobile App': '📱',
        'IE': '🌐',
};
const OS_ICONS = {
        'Windows': '🪟',
        'Mac': '🍎',
        'macOS': '🍎',
        'iOS': '📱',
        'iPadOS': '📱',
        'Android': '🤖',
        'Chrome OS': '🌐',
        'GNU/Linux': '🐧',
        'Linux': '🐧',
        'Ubuntu': '🐧',
        'Fedora': '🐧',
        'Arch Linux': '🐧',
        'openSUSE': '🐧',
        'Debian': '🐧',
        'Linux Mint': '🐧',
        'Manjaro': '🐧',
        'CentOS': '🐧',
        'Red Hat': '🐧',
};
const getDeviceRowIcon = (name, kind) => {
        if (kind === 'browser') return BROWSER_ICONS[name] || '🌐';
        if (kind === 'os') return OS_ICONS[name] || '💻';
        return '';
};

/**
 * DetailsModal Component
 * Full-screen modal with search, sorting, and scrollable table
 * 
 * @param {boolean} isOpen - Modal open state
 * @param {function} onClose - Close callback
 * @param {string} title - Modal title
 * @param {string} type - Data type (pages, sources, locations)
 * @param {array} data - Array of records
 * @param {boolean} loading - Loading state
 * @param {string} searchQuery - Current search query
 * @param {function} onSearchChange - Search change handler
 * @param {object} sortConfig - Sort configuration {key, direction}
 * @param {function} onSort - Sort handler
 * @param {string} activeTab - Active tab for locations (countries, regions, cities)
 */
const DetailsModal = ({
        isOpen,
        onClose,
        title,
        type,
        data = [],
        loading = false,
        searchQuery,
        onSearchChange,
        sortConfig,
        onSort,
        activeTab,
        period,
        os,
        websiteId,
        websiteDomain,
        channel,
        device,
        browser,
        location,
        startDate,
        endDate,
        // Pagination props
        hasMore = false,
        onLoadMore,
        loadingMore = false,
        totalCount = 0
}) => {
        const searchInputRef = useRef(null);
        const navigate = useNavigate();

        // Handle "/" hotkey for search
        useEffect(() => {
                const handleKeyDown = (e) => {
                        if (e.key === '/' && isOpen) {
                                e.preventDefault();
                                searchInputRef.current?.focus();
                        }
                        if (e.key === 'Escape' && isOpen) {
                                onClose();
                        }
                };

                window.addEventListener('keydown', handleKeyDown);
                return () => window.removeEventListener('keydown', handleKeyDown);
        }, [isOpen, onClose]);

        // Focus search input when modal opens
        useEffect(() => {

                console.log("===", location, websiteId, period);

                if (isOpen) {
                        setTimeout(() => searchInputRef.current?.focus(), 100);
                }
        }, [isOpen]);


        const handleSourcesClick = (sourceName) => {
                // Build URL with unified filter format
                let url = `/analytics/${websiteId}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                
                // Determine the filter type based on activeTab
                let filterType = 'source';
                if (activeTab === 'channels') {
                        filterType = 'channel';
                } else if (activeTab === 'campaigns') {
                        filterType = 'campaign';
                }
                
                // Add the filter using unified f= format
                url += `&f=is,${filterType},${encodeURIComponent(sourceName)}`;
                
                // Keep existing filters
                if (location) {
                        url += `&f=is,country,${encodeURIComponent(location)}`;
                }
                if (device) {
                        url += `&f=is,device,${encodeURIComponent(device)}`;
                }
                if (os) {
                        url += `&f=is,os,${encodeURIComponent(os)}`;
                }
                if (browser) {
                        url += `&f=is,browser,${encodeURIComponent(browser)}`;
                }
                onClose();
                navigate(url);
        };

        // Handle location click to filter dashboard
        const handleLocationClick = (locationType, locationName) => {

                console.log(websiteId);
                console.log("-----------------------");
                let url = `/analytics/${websiteId}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                if (channel) {
                        url += `&channel=${encodeURIComponent(channel)}`;
                }
                if (device) {
                        url += `&device=${encodeURIComponent(device)}`;
                }
                if (os) {
                        url += `&os=${encodeURIComponent(os)}`;
                }
                if (browser) {
                        url += `&browser=${encodeURIComponent(browser)}`;
                }

                url += `&location=${locationType}:${encodeURIComponent(locationName)}`;

                onClose();

                navigate(url);
        };
        // Handle browser / OS click in the devices modal: adds `f=is,browser,<name>`
        // (or `f=is,os,<name>`) to the URL, preserving other active filters. Mirrors
        // the navigation that DevicesInsights' inline onBrowserClick/onOsClick run,
        // so clicking the same row inline or in the modal lands on the same view.
        const handleDeviceClick = (name, kind) => {
                let url = `/analytics/${websiteId}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                if (channel) url += `&f=is,channel,${encodeURIComponent(channel)}`;
                if (location) url += `&f=is,country,${encodeURIComponent(location)}`;
                if (device) url += `&f=is,device,${encodeURIComponent(device)}`;
                // Don't re-add the same filter twice. If we're already filtered by
                // browser and the user clicks a different browser in the modal,
                // the new value replaces the old (the filter is "is browser X");
                // similarly for OS.
                if (os && kind !== 'os') url += `&f=is,os,${encodeURIComponent(os)}`;
                if (browser && kind !== 'browser') url += `&f=is,browser,${encodeURIComponent(browser)}`;
                url += `&f=is,${kind},${encodeURIComponent(name)}`;
                onClose();
                navigate(url);
        };

        // Handle page click to filter dashboard
        const handlePageClick = (pagePath, pageType = 'page') => {
                let url = `/analytics/${websiteId}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                if (channel) {
                        url += `&channel=${encodeURIComponent(channel)}`;
                }
                if (location) {
                        url += `&location=${encodeURIComponent(location)}`;
                }
                if (device) {
                        url += `&device=${encodeURIComponent(device)}`;
                }
                if (os) {
                        url += `&os=${encodeURIComponent(os)}`;
                }
                if (browser) {
                        url += `&browser=${encodeURIComponent(browser)}`;
                }
                
                // Add page filter
                url += `&f=is,${pageType},${encodeURIComponent(pagePath)}`;
                
                onClose();
                navigate(url);
        };


        // Render sort icon
        const SortIcon = ({ column }) => {
                if (sortConfig.key !== column) {
                        return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
                }
                return sortConfig.direction === 'asc'
                        ? <ArrowUp className="w-4 h-4 text-blue-600" />
                        : <ArrowDown className="w-4 h-4 text-blue-600" />;
        };

        // Render table headers based on type
        const renderHeaders = () => {
                if (type === 'pages') {
                        return (
                                <tr className="border-b-2 border-gray-200">
                                        <th
                                                className="py-4 px-6 swidth uppercase tracking-wider cursor-pointer hover:bg-gray-50`"
                                                onClick={() => onSort('page_url')}
                                        >
                                                <div className="flex items-center gap-2">
                                                        Page URL
                                                        <SortIcon column="page_url" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6"
                                                onClick={() => onSort('visitors')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Visitors
                                                        <SortIcon column="visitors" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('pageviews')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Pageviews
                                                        <SortIcon column="pageviews" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('bounce_rate')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Bounce Rate
                                                        <SortIcon column="bounce_rate" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('avg_time_on_page')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Avg Time
                                                        <SortIcon column="avg_time_on_page" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('scroll_depth')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Scroll Depth
                                                        <SortIcon column="scroll_depth" />
                                                </div>
                                        </th>
                                </tr>
                        );
                } else if (type === 'sources') {
                        return (
                                <tr className="border-b-2 border-gray-200">
                                        <th
                                                className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('referrer')}
                                        >
                                                <div className="flex items-center gap-2">
                                                        Source
                                                        <SortIcon column="referrer" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('visitors')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Visitors
                                                        <SortIcon column="visitors" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('pageviews')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Pageviews
                                                        <SortIcon column="pageviews" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('bounce_rate')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Bounce Rate
                                                        <SortIcon column="bounce_rate" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('visit_duration')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Visit Duration
                                                        <SortIcon column="visit_duration" />
                                                </div>
                                        </th>
                                </tr>
                        );
                } else if (type === 'locations') {

                        // Determine which column to show based on activeTab
                        const locationLabel = activeTab === 'countries' ? 'Country' :
                                activeTab === 'regions' ? 'Region' :
                                        activeTab === 'cities' ? 'City' : 'Location';
                        const sortColumn = activeTab === 'countries' ? 'country' :
                                activeTab === 'regions' ? 'region' :
                                        activeTab === 'cities' ? 'city' : 'country';

                        return (
                                <tr className="border-b-2 border-gray-200">
                                        <th
                                                className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort(sortColumn)}
                                        >
                                                <div className="flex items-center gap-2">
                                                        {locationLabel}
                                                        <SortIcon column={sortColumn} />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('visitors')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Visitors
                                                        <SortIcon column="visitors" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('pageviews')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Pageviews
                                                        <SortIcon column="pageviews" />
                                                </div>
                                        </th>
                                </tr>
                        );
                } else if (type === 'devices') {
                        // activeTab carries 'browser' or 'os' — drives the column label.
                        const deviceLabel = activeTab === 'os' ? 'Operating System' : 'Browser';
                        const sortColumn = 'name';
                        return (
                                <tr className="border-b-2 border-gray-200">
                                        <th
                                                className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort(sortColumn)}
                                        >
                                                <div className="flex items-center gap-2">
                                                        {deviceLabel}
                                                        <SortIcon column={sortColumn} />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('visitors')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Visitors
                                                        <SortIcon column="visitors" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('percentage')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        % Share
                                                        <SortIcon column="percentage" />
                                                </div>
                                        </th>
                                </tr>
                        );
                } else if (type === 'goals') {
                        return (
                                <tr className="border-b-2 border-gray-200">
                                        <th
                                                className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('goal')}
                                        >
                                                <div className="flex items-center gap-2">
                                                        Goal
                                                        <SortIcon column="goal" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('unique_visitors')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Uniques
                                                        <SortIcon column="unique_visitors" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('total_conversions')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        Total
                                                        <SortIcon column="total_conversions" />
                                                </div>
                                        </th>
                                        <th
                                                className="py-4 px-6 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => onSort('conversion_rate')}
                                        >
                                                <div className="flex items-center justify-end gap-2">
                                                        CR
                                                        <SortIcon column="conversion_rate" />
                                                </div>
                                        </th>
                                </tr>
                        );
                }
        };

        // Render table rows based on type
        const renderRow = (row, index) => {
                if (type === 'pages') {
                        // Determine page type based on activeTab
                        const pageType = activeTab === 'entry' ? 'entry_page' : activeTab === 'exit' ? 'exit_page' : 'page';
                        return (
                                <tr key={index} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span 
                                                                onClick={() => handlePageClick(row.page_url, pageType)}
                                                                style={{ cursor: 'pointer', color: '#6366f1' }}
                                                                title={row.page_url}
                                                        >
                                                                {row.page_url}
                                                        </span>
                                                        {websiteDomain && (
                                                                <a
                                                                        href={`${websiteDomain.replace(/\/$/, '')}${row.page_url}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="external-link-icon"
                                                                        title={`Open ${row.page_url} on site`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                >
                                                                        <ExternalLink size={14} />
                                                                </a>
                                                        )}
                                                </div>
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.visitors?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.pageviews?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.bounce_rate}%
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.avg_time_on_page}s
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.scroll_depth}%
                                        </td>
                                </tr>
                        );
                } else if (type === 'sources') {
                        return (
                                <tr key={index} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                                                <span
                                                        onClick={() => handleSourcesClick(row.referrer)}
                                                        style={{ cursor: 'pointer', color: '#6366f1', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                                        className="hover:underline"
                                                >
                                                        {/* Real favicon (DuckDuckGo icon service) when the backend
                                                            supplied a domain; hidden on load error. */}
                                                        {row.favicon && (
                                                                <img
                                                                        src={`https://icons.duckduckgo.com/ip3/${row.favicon}.ico`}
                                                                        alt=""
                                                                        width={16}
                                                                        height={16}
                                                                        loading="lazy"
                                                                        style={{ borderRadius: '3px', display: 'block' }}
                                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                />
                                                        )}
                                                        {row.name || row.referrer}
                                                </span>
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.visitors?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.pageviews?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.bounce_rate}%
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {formatDurationShort(row.visit_duration)}
                                        </td>
                                </tr>
                        );
                } else if (type === 'locations') {
                
                        // Show only the relevant location field based on activeTab
                        let locationText;
                        let locationText1;
                        let activeTab1;
                        if (activeTab === 'countries') {
                                locationText = getCountryName(row.country) || 'Unknown';
                                locationText1 = row.country;
                                activeTab1 ="country";
                        } else if (activeTab === 'regions') {
                                // Show only region name (without country)
                                locationText = row.region || 'Unknown';
                                locationText1 = row.region;
                                activeTab1 = "region";

                        } else if (activeTab === 'cities') {
                                // Show only city name (without region/country)
                                locationText = row.city || 'Unknown';
                                locationText1 = row.city;
                                activeTab1 = "city";

                        } else {
                                // Default: show full location
                                locationText = [row.city, row.region, row.country].filter(Boolean).join(', ') || 'Unknown';
                        }

                        return (

                                <tr key={index} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                                                <div onClick={() => handleLocationClick(activeTab1, locationText1)}>
                                                        {/* Country flag on every tab that has row.country
                                                            — countries always, regions/cities when the
                                                            backend sends country context (which it now
                                                            does; older backends without the field render
                                                            the row without a flag as before). */}
                                                        {row.country && (
                                                                <span className="country-flag" style={{ marginRight: '8px' }}>{getCountryFlag(row.country)}</span>
                                                        )}

                                                        <span>{locationText}</span>

                                                </div>
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.visitors?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.pageviews?.toLocaleString()}
                                        </td>
                                </tr>
                        );
                } else if (type === 'devices') {
                        // activeTab = 'browser' | 'os' (set by DevicesInsights).
                        const kind = activeTab === 'os' ? 'os' : 'browser';
                        return (
                                <tr key={index} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                                                <span
                                                        onClick={() => handleDeviceClick(row.name, kind)}
                                                        style={{ cursor: 'pointer', color: '#6366f1', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                                        className="hover:underline"
                                                >
                                                        <span style={{ fontSize: '16px' }}>{getDeviceRowIcon(row.name, kind)}</span>
                                                        {row.name}
                                                </span>
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.visitors?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.percentage?.toFixed(1)}%
                                        </td>
                                </tr>
                        );
                } else if (type === 'goals') {
                        return (
                                <tr key={index} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                                                {row.goal}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.unique_visitors?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.total_conversions?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-gray-600 text-right">
                                                {row.conversion_rate}%
                                        </td>
                                </tr>
                        );
                }
        };

        return (
                <AnimatePresence>
                        {isOpen && (
                                <>
                                        {/* Backdrop */}
                                        <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="fixed inset-0 bg-black/50 z-50"
                                                onClick={onClose}
                                        />

                                        {/* Modal */}
                                        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                                                <motion.div
                                                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                                        transition={{ type: 'spring', duration: 0.3 }}
                                                        className="bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden w-full"
                                                        style={{ maxWidth: '1024px', maxHeight: '70vh' }}
                                                >
                                                        {/* Sticky Header */}
                                                        <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
                                                                <div className="flex items-center justify-between px-6 py-4">
                                                                        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                                                                        <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                onClick={onClose}
                                                                                className="rounded-full hover:bg-gray-100"
                                                                        >
                                                                                <X className="h-5 w-5" />
                                                                        </Button>
                                                                </div>

                                                                {/* Search Bar */}
                                                                <div className="px-6 pb-4">
                                                                        <div className="relative">
                                                                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                                                <Input
                                                                                        ref={searchInputRef}
                                                                                        type="text"
                                                                                        placeholder="Press / to search"
                                                                                        value={searchQuery}
                                                                                        onChange={(e) => onSearchChange(e.target.value)}
                                                                                        className="pl-10 pr-4 py-2 w-full border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                                />
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        {/* Scrollable Content */}
                                                        <div className="flex-1 overflow-y-auto">
                                                                {loading ? (
                                                                        <div className="flex items-center justify-center h-64">
                                                                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                                                        </div>
                                                                ) : (
                                                                                <table className="w-full ellipsis-table">
                                                                                <thead className="bg-gray-50 sticky top-0">
                                                                                        {renderHeaders()}
                                                                                </thead>
                                                                                <tbody className="bg-white divide-y divide-gray-100">
                                                                                        {data.length === 0 ? (
                                                                                                <tr>
                                                                                                        <td colSpan="6" className="py-12 px-6 text-center text-sm text-gray-500">
                                                                                                                {searchQuery ? 'No results found' : 'No data available'}
                                                                                                        </td>
                                                                                                </tr>
                                                                                        ) : (
                                                                                                data.map((row, index) => renderRow(row, index))
                                                                                        )}
                                                                                </tbody>
                                                                        </table>
                                                                )}
                                                        </div>

                                                        {/* Footer with count and Load More */}
                                                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-3">
                                                                <div className="flex items-center justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                                Showing {data.length} {totalCount > 0 ? `of ${totalCount}` : ''} {data.length === 1 ? 'result' : 'results'}
                                                                        </p>
                                                                        {hasMore && onLoadMore && (
                                                                                <Button
                                                                                        variant="outline"
                                                                                        size="sm"
                                                                                        onClick={onLoadMore}
                                                                                        disabled={loadingMore}
                                                                                        className="text-sm"
                                                                                >
                                                                                        {loadingMore ? 'Loading...' : 'Load More'}
                                                                                </Button>
                                                                        )}
                                                                </div>
                                                        </div>
                                                </motion.div>
                                        </div>
                                </>
                        )}
                </AnimatePresence>
        );
};

export default DetailsModal;
